<script setup lang="ts">
import ExternalLink from "../../../assets/icons/externalLink.svg";
import { ref, computed } from "vue";
import type { IListing } from "../../../types.ts";
import Star from "../../../assets/icons/star.svg";
import { useAdminStore } from "@/store/admin.ts";
import { getS3URL, generateInitialsAvatar } from "@/utils/helpers.ts";
import { UiLink } from "@shopexperts/shared";

const props = defineProps<{
  listing: IListing,
  currentFilters: Record<string, any>
}>()

const emit = defineEmits<{
  (e: 'openLoginModal', expert: IListing): void
}>()

const adminStore = useAdminStore();
const isLoading = ref(false);

const handleLoginAs = () => {
  emit('openLoginModal', props.listing);
}

// Get the actual status from status_formatted
const expertStatus = computed(() => {
  return props.listing.status_formatted?.toLowerCase() || '';
});

// Handler for activating applications
const handleActivateApplication = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    await adminStore.activateNewExpertApplication(props.listing.id);
  } catch (error: any) {
    console.error('Failed to activate application:', error);
    alert(error.response?.data?.error || 'Failed to activate application');
  } finally {
    isLoading.value = false;
  }
}

// Handler for publishing
const handlePublish = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    await adminStore.updateNewExpertStatus(props.listing.id, 'publish');
  } catch (error: any) {
    console.error('Failed to publish:', error);
    alert(error.response?.data?.error || 'Failed to publish');
  } finally {
    isLoading.value = false;
  }
}

// Handler for unpublishing
const handleUnpublish = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    await adminStore.updateNewExpertStatus(props.listing.id, 'unpublish');
  } catch (error: any) {
    console.error('Failed to unpublish:', error);
    alert(error.response?.data?.error || 'Failed to unpublish');
  } finally {
    isLoading.value = false;
  }
}

// Handler for resending activation email
const handleResendActivation = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    await adminStore.resendExpertActivation(props.listing.id);
    alert('Activation email resent successfully.');
  } catch (error: any) {
    console.error('Failed to resend activation:', error);
    alert(error.response?.data?.error || 'Failed to resend activation email');
  } finally {
    isLoading.value = false;
  }
}

// Helper to get proper status color classes
const getStatusClasses = computed(() => {
  const status = expertStatus.value;
  
  if (status === 'application') {
    return 'text-warning bg-warning-light';
  } else if (status === 'draft') {
    return 'text-pending bg-pending-light';
  } else if (status === 'published') {
    return 'text-success bg-success-light';
  } else if (status === 'unpublished') {
    return 'text-error bg-error-light';
  }
  
  return 'text-link bg-link-light';
});

// Helper to get proper status display text
const getStatusDisplay = computed(() => {
  return props.listing.status_formatted || 'Unknown';
});
</script>

<template>
  <div class="bg-white border rounded-md shadow-xs p-card-padding mb-5">
    <!-- Header -->
    <div class="grid grid-cols-5 gap-6 mb-5 items-start">
      <div class="flex gap-4 col-span-2">
        <!-- Avatar with initials or image -->
        <div class="w-16 h-16 rounded-full overflow-hidden">
          <!-- Show actual image if photo exists -->
          <img
              v-if="listing.photo && listing.photo !== null && listing.photo !== ''"
              :src="getS3URL(listing.photo)"
              alt="Expert avatar"
              class="w-full h-full rounded-full object-cover"
          />
          <!-- Show initials avatar if no photo -->
          <div
              v-else
             :class="[
               'w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-h3',
               generateInitialsAvatar(listing.display_name).bgColor
             ]"
          >
           {{ generateInitialsAvatar(listing.display_name).initials }}
          </div>
        </div>
        <div>
          <div class="flex items-center gap-2">
            <p class="text-primary font-medium">{{ listing.display_name }}</p>
            <h5
                class="font-medium px-2 py-0.5 rounded-sm capitalize"
                :class="getStatusClasses"
                :title="`Status: ${expertStatus}`"
            >
              {{ getStatusDisplay }}
            </h5>
          </div>
          <div class="space-y-1">
            <h4 class="font-light">{{ listing.expert_type_formatted }}</h4>
            <div v-if="listing.total_reviews_safe && listing.total_reviews_safe > 0" class="flex items-center gap-1 text-h4">
              <Star class="w-4 h-4 text-success shrink-0" />
              <span class="text-primary font-medium">{{ listing.average_rating_safe }}</span>
              <span class="text-light-grey">({{ listing.total_reviews_safe }} reviews)</span>
            </div>
            <a :href="`mailto:${listing.email}`" class="text-h4 text-link hover:underline">
              {{ listing.email }}
            </a>
            <a :href="listing.url.startsWith('http') ? listing.url : `https://${listing.url}`" target="_blank" class="flex items-center gap-1 text-h4 text-link hover:underline">
              {{ listing.store_title }}
              <ExternalLink />
            </a>
          </div>
        </div>
      </div>

      <h4 class="flex flex-col">
        <span>{{ listing.job_title_formatted }}</span>
        <span>{{ listing.country_formatted }}</span>
        <span>{{ listing.language_formatted }}</span>
      </h4>

      <h4 class="flex flex-col">
        <span>Min. Project Budget</span>
        <span class="font-medium text-h3">{{ listing.hourly_rate_formatted }}</span>
      </h4>

      <div class="font-light text-h5">
        <div class="flex flex-col items-end space-y-2">
          <div class="flex items-center space-x-2">
            <!-- Activate Application Button (for application status) -->
            <button
                v-if="expertStatus === 'application'"
                @click="handleActivateApplication"
                :disabled="isLoading"
                class="bg-success text-white text-h4 py-2 px-4 rounded-sm hover:bg-success-dark disabled:opacity-50"
            >
              {{ isLoading ? 'Activating...' : 'Activate' }}
            </button>

            <!-- Publish Button (for draft or unpublished status) -->
            <button
                v-if="expertStatus === 'draft' || expertStatus === 'unpublished'"
                @click="handlePublish"
                :disabled="isLoading"
                class="bg-primary text-white text-h4 py-2 px-4 rounded-sm hover:bg-primary-dark disabled:opacity-50"
            >
              {{ isLoading ? 'Publishing...' : 'Publish' }}
            </button>

            <!-- Unpublish Button (for published status) -->
            <button
                v-if="expertStatus === 'published'"
                @click="handleUnpublish"
                :disabled="isLoading"
                class="bg-error text-white text-h4 py-2 px-4 rounded-sm hover:bg-error-dark disabled:opacity-50"
            >
              {{ isLoading ? 'Unpublishing...' : 'Unpublish' }}
            </button>


            <!-- Login As Button -->
            <button
                @click="handleLoginAs"
                :disabled="isLoading"
                class="bg-link text-white text-h4 py-2 px-4 rounded-sm hover:bg-link-dark disabled:opacity-50"
            >
              Login As
            </button>

            </div>
              <!-- Resend Activation Email Button (for all activated statuses) -->
              <UiLink
                href="#"
                label="Resend Activation Email"
                size="sm"
                :disabled="isLoading"
                :loading="isLoading"
                @click="handleResendActivation"
              >
              </UiLink>
          <h5 class="text-right text-light-grey">Submitted on: {{ listing.status_updated_at_formatted }}</h5>
        </div>
      </div>
    </div>

    <!--  Body  -->
    <div v-if="listing.services_offered_safe && listing.services_offered_safe.length > 0">
      <h4 class="text-gray-500 font-normal pb-2">Services Offered</h4>
      <div class="flex flex-row">
        <h4
            class="font-normal py-1 px-2 border mr-2 rounded-full"
            v-for="(service, index) in listing.services_offered_safe"
            :key="index"
        >
          {{ service }}
        </h4>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>

